<template>
  <div class="language-switcher">
    <span class="globe-icon">🌐</span>
    <select
      :value="currentLocale"
      @change="changeLanguage"
      class="form-select form-select-sm"
      :aria-label="$t('common.selectLanguage')"
    >
      <option
        v-for="(lang, code) in sortedLanguages"
        :key="code"
        :value="code"
      >
        {{ lang.nativeName }}
      </option>
    </select>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { Languages } from '@/data/Language';
import { useHaptics } from '@/composables/useHaptics.js';
import { applyVisitorLocalePick } from '@/locales';

export default {
  name: 'LanguageSwitcher',
  setup() {
    const { locale } = useI18n();
    const { impactLight } = useHaptics();

    // Sort languages alphabetically by name
    const sortedLanguages = computed(() => {
      return Object.fromEntries(
        Object.entries(Languages).sort((a, b) =>
          a[1].name.localeCompare(b[1].name)
        )
      );
    });

    const changeLanguage = async (event) => {
      const requested = event.target.value;

      // This switcher only renders logged out (AppNavigation gates it on
      // !isLoggedIn), so there is no nativeLanguage to derive from and the
      // visitor's pick is what preferredLocale is allowed to hold. The helper
      // fetches the bundle before applying it -- assigning locale.value here
      // instead would switch to a locale with no messages, which vue-i18n
      // reports as nothing at all -- and owns the cache rule.
      await applyVisitorLocalePick(requested);

      // Completed selection: confirm with a light haptic on native platforms.
      impactLight();
    };

    return {
      currentLocale: locale,
      sortedLanguages,
      changeLanguage
    };
  }
};
</script>

<style scoped>
.language-switcher {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 200px;
}

.globe-icon {
  font-size: 1.25rem;
  opacity: 0.7;
}

.form-select-sm {
  font-size: 0.875rem;
  padding: 0.375rem 2rem 0.375rem 0.75rem;
  border-radius: var(--al-radius);
  border: 1px solid var(--al-border);
  background-color: var(--al-surface-raised);
  cursor: pointer;
}

.form-select-sm:focus {
  border-color: var(--al-primary);
  outline: 0;
  box-shadow: 0 0 0 0.25rem color-mix(in srgb, var(--al-primary) 25%, transparent);
}

@media (max-width: 768px) {
  .language-switcher {
    min-width: 150px;
  }

  .globe-icon {
    font-size: 1.1rem;
  }
}
</style>
